import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Circle,
  CheckCircle,
  Star,
  RadioButtonChecked,
  Person,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import styled from "styled-components";
import { RequestState } from "../../modules/constants";

const MealCard = styled(Card)`
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const MealImage = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
`;

const MealTime = styled(Typography)`
  font-size: 0.75rem;
  font-weight: bold;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CalorieChip = styled(Chip)`
  font-size: 0.75rem;
  height: 20px;
`;

const getDayName = (date) => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return days[new Date(date).getDay()];
};

const getMealIcon = (mealType) => {
  switch (mealType?.toLowerCase()) {
    case "breakfast":
      return <Circle sx={{ fontSize: 16 }} />;
    case "lunch":
      return <CheckCircle sx={{ fontSize: 16 }} />;
    case "dinner":
      return <Star sx={{ fontSize: 16 }} />;
    case "snack":
      return <RadioButtonChecked sx={{ fontSize: 16 }} />;
    default:
      return <Person sx={{ fontSize: 16 }} />;
  }
};

const getMealTypeColor = (mealType) => {
  switch (mealType?.toLowerCase()) {
    case "breakfast":
      return "#ff9800";
    case "lunch":
      return "#4caf50";
    case "dinner":
      return "#3f51b5";
    case "snack":
      return "#e91e63";
    default:
      return "#757575";
  }
};

const getUpcomingMeals = (meals) => {
  const today = new Date();
  const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

  return meals
    .filter((meal) => {
      const mealDate = new Date(meal.date);
      return mealDate >= today && mealDate <= nextWeek;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 5); // Show next 5 meals
};

export const WeeklyMealsWidget = observer(({ controller }) => {
  const { getWeeklyMeals, getWeeklyMealsRequestState } = controller;
  const isLoading = getWeeklyMealsRequestState === RequestState.REQUESTED;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const upcomingMeals = getUpcomingMeals(getWeeklyMeals);

  if (upcomingMeals.length === 0) {
    return (
      <Card>
        <CardHeader
          title="Weekly Meal Plan"
          subheader="Your upcoming meals for the week"
        />
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <Circle sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No meals planned yet
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
            >
              Start planning your meals to see them here
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Weekly Meal Plan"
        subheader={`${upcomingMeals.length} upcoming meals planned`}
      />
      <CardContent>
        <Grid container spacing={2}>
          {upcomingMeals.map((meal, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <MealCard variant="outlined">
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Avatar
                      sx={{
                        bgcolor: getMealTypeColor(meal.type),
                        width: 24,
                        height: 24,
                        mr: 1,
                      }}
                    >
                      {getMealIcon(meal.type)}
                    </Avatar>
                    <Box flex={1}>
                      <MealTime variant="caption">
                        {getDayName(meal.date)} • {meal.type} • {new Date(meal.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </MealTime>
                    </Box>
                    {meal.calories && (
                      <CalorieChip
                        label={`${meal.calories} cal`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {meal.image && (
                    <MealImage
                      src={meal.image}
                      alt={meal.name}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}

                  <Typography
                    variant="subtitle2"
                    sx={{ mt: 1, fontWeight: "bold" }}
                  >
                    {meal.name || "Untitled Meal"}
                  </Typography>

                  {meal.description && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 0.5 }}
                    >
                      {meal.description.length > 60
                        ? `${meal.description.substring(0, 60)}...`
                        : meal.description}
                    </Typography>
                  )}

                  {meal.tags && meal.tags.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                      {meal.tags.slice(0, 3).map((tag, tagIndex) => (
                        <Chip
                          key={tagIndex}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      ))}
                      {meal.tags.length > 3 && (
                        <Chip
                          label={`+${meal.tags.length - 3}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      )}
                    </Box>
                  )}
                </CardContent>
              </MealCard>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
});

export default WeeklyMealsWidget;
